import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { UploadService, DirectUploadError } from '../services/uploadService';
import { setUploadActive } from '../services/uploadActivity';
import { notificationService } from '../services/notificationService';
import { UploadMetadata, UploadQueueItem } from '../types/Upload';

/**
 * App-root owner of the upload pipeline. Lives above the Router so uploads survive
 * any SPA navigation; a beforeunload guard warns before hard navigation/refresh/close,
 * which production logs showed silently killing in-flight files.
 *
 * Per file: presign → direct PUT to blob storage → confirm. If the direct path fails
 * (storage firewall closed, missing RBAC, CORS), it falls back to the relay endpoint
 * for that file and disables the direct path for the rest of the session.
 */

// One at a time: a relay upload holds a server request thread for the entire body transfer, so
// 4-way concurrency split the client uplink four ways and pushed some POSTs past the 230s gateway
// timeout (504 → false "failed", while the server still committed the row). Serial uploads keep
// each file fast and well inside the gateway window. Raise only with a faster transport (direct-to-blob).
const UPLOAD_CONCURRENCY = 1;
const RELAY_ATTEMPTS = 2;
const RELAY_RETRY_DELAY_MS = 2000;
// Lost-response reconcile: after a relay timeout the server may still be committing the row, so
// probe the idempotency key a few times with a short gap before deciding a file actually failed.
const RECONCILE_PROBE_ATTEMPTS = 3;
const RECONCILE_PROBE_DELAY_MS = 2000;
// Session-end reconcile: the inline probe above can give up before a *starved* server commits the
// row (a slow upload cut off at the gateway timeout lands seconds later). So after the whole queue
// drains, re-probe every file that ended in a transport error, with a longer backoff. This is the
// authoritative pass that flips a phantom "failed" (row exists server-side) to completed, keeping
// the on-screen count truthful.
const SESSION_RECONCILE_ATTEMPTS = 4;
const SESSION_RECONCILE_DELAY_MS = 3000;

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// Only ambiguous transport failures can be phantom successes. A quota or validation rejection never
// created a row, so those stay real errors and are not flickered through the "verifying" state.
const isReconcilableError = (message?: string): boolean => {
  if (!message) return false;
  const m = message.toLowerCase();
  return m.includes('network') || m.includes('timeout');
};

interface UploadContextValue {
  items: UploadQueueItem[];
  isUploading: boolean;
  activeCount: number;
  completedCount: number;
  errorCount: number;
  overallProgress: number;
  dispatch: (files: File[], metadata: UploadMetadata) => void;
}

const UploadContext = createContext<UploadContextValue | undefined>(undefined);

type QueueEntry = Pick<UploadQueueItem, 'id' | 'file' | 'dedupeId'>;

export const UploadContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<UploadQueueItem[]>([]);
  // Mirror of items for reads inside async worker/drain code (avoids stale closures).
  const itemsRef = useRef<UploadQueueItem[]>([]);
  itemsRef.current = items;

  const queueRef = useRef<QueueEntry[]>([]);
  const cursorRef = useRef(0);
  const workersRef = useRef(0);
  const directDisabledRef = useRef(false);
  const metadataRef = useRef<UploadMetadata>({ callType: '911' });
  // Per-session outcome tally — drives the completion toast and the session-complete
  // signal without reading React state from inside the worker loop.
  const statsRef = useRef({ completed: 0, error: 0 });

  const updateItem = useCallback((id: string, patch: Partial<UploadQueueItem>) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }, []);

  // Runs one file end-to-end and returns its terminal outcome so the caller can tally it.
  const processEntry = useCallback(async (entry: QueueEntry): Promise<'completed' | 'error'> => {
    const { id, file, dedupeId } = entry;
    let presignedCallId: string | undefined;

    // ── Direct path: presign → PUT to blob → confirm ──
    if (!directDisabledRef.current) {
      try {
        updateItem(id, { status: 'presigning' });
        const presign = await UploadService.presignFile(file);

        if (presign.quotaExceeded) {
          updateItem(id, { status: 'error', errorMessage: presign.errorMessage ?? 'Daily upload limit reached' });
          return 'error';
        }

        if (presign.directUploadDisabled) {
          // Platform kill-switch is off — skip presign for every remaining file this session.
          directDisabledRef.current = true;
        }

        if (presign.success) {
          presignedCallId = presign.callId;
          updateItem(id, { status: 'uploading', progress: 0 });

          await UploadService.uploadToBlob(file, presign.sasUrl, (progress) =>
            updateItem(id, { progress })
          );

          updateItem(id, { status: 'confirming', progress: 100 });
          const confirmed = await UploadService.confirmFile(presign.callId);
          if (confirmed.success) {
            updateItem(id, { status: 'completed', progress: 100 });
            return 'completed';
          }
        }
        // presign returned a non-quota failure or confirm failed — fall through to relay
      } catch (error) {
        if (error instanceof DirectUploadError) {
          // Storage unreachable from this browser (firewall/CORS/RBAC) — stop
          // presigning for the rest of the session and use the relay for everything.
          directDisabledRef.current = true;
        }
      }

      if (presignedCallId) {
        // Frees the quota reservation so the relay attempt below isn't double-counted.
        await UploadService.abandonFile(presignedCallId);
      }
    }

    // ── Relay path (fallback, or primary when direct upload is disabled) ──
    for (let attempt = 1; attempt <= RELAY_ATTEMPTS; attempt++) {
      try {
        updateItem(id, { status: 'uploading', progress: 0 });
        const result = await UploadService.uploadFileViaRelay(file, metadataRef.current, dedupeId, (progress) =>
          updateItem(id, { progress })
        );

        if (result.success) {
          updateItem(id, { status: 'completed', progress: 100 });
          return 'completed';
        }

        const message = result.errorMessage ?? 'Upload failed';
        const isQuota = message.toLowerCase().includes('limit');
        if (isQuota || attempt === RELAY_ATTEMPTS) {
          updateItem(id, { status: 'error', errorMessage: message });
          return 'error';
        }
      } catch (error) {
        if (attempt === RELAY_ATTEMPTS) {
          let landed = false;
          for (let probe = 0; probe < RECONCILE_PROBE_ATTEMPTS && !landed; probe++) {
            if (probe > 0) await sleep(RECONCILE_PROBE_DELAY_MS);
            landed = await UploadService.checkUploadStatus(dedupeId);
          }
          if (landed) {
            updateItem(id, { status: 'completed', progress: 100 });
            return 'completed';
          }
          updateItem(id, {
            status: 'error',
            errorMessage: error instanceof Error ? error.message : 'Upload failed',
          });
          return 'error';
        }
      }
      await sleep(RELAY_RETRY_DELAY_MS);
    }

    return 'error';
  }, [updateItem]);

  const onDrain = useCallback(async () => {
    // ── Session-end reconcile ──
    // A file that reported a transport error may actually have committed server-side (its response
    // was lost to a gateway timeout). Re-check each against the idempotency key before settling the
    // final tally, so a file that landed is never left showing "failed".
    const candidates = itemsRef.current.filter(
      (i) => i.status === 'error' && isReconcilableError(i.errorMessage)
    );
    if (candidates.length > 0) {
      const verifyIds = new Set(candidates.map((c) => c.id));
      setItems((prev) => prev.map((i) => (verifyIds.has(i.id) ? { ...i, status: 'verifying' } : i)));

      const landedIds = new Set<string>();
      for (const item of candidates) {
        let landed = false;
        for (let probe = 0; probe < SESSION_RECONCILE_ATTEMPTS && !landed; probe++) {
          if (probe > 0) await sleep(SESSION_RECONCILE_DELAY_MS);
          landed = await UploadService.checkUploadStatus(item.dedupeId);
        }
        if (landed) landedIds.add(item.id);
      }

      // Re-tally so the completion toast and the session-complete signal reflect the true outcome.
      statsRef.current.completed += landedIds.size;
      statsRef.current.error -= landedIds.size;
      setItems((prev) =>
        prev.map((i) => {
          if (!verifyIds.has(i.id)) return i;
          return landedIds.has(i.id)
            ? { ...i, status: 'completed', progress: 100 }
            : { ...i, status: 'error' };
        })
      );
    }

    const { completed, error } = statsRef.current;
    statsRef.current = { completed: 0, error: 0 };
    // Release file references and rewind the cursor for the next session.
    queueRef.current = [];
    cursorRef.current = 0;

    // Session finished: drop completed rows so the Upload page's progress list clears
    // (those calls are now tracked on the home page). Keep errors visible so the user
    // can still see and act on failures; they clear on the next dispatch.
    setItems((prev) => prev.filter((item) => item.status === 'error'));

    if (completed > 0) {
      // Tells the sweep the session is done so it submits ONE Speech batch now
      // instead of waiting for the MaxHold age trigger.
      await UploadService.completeUploadSession();
    }

    if (error > 0) {
      notificationService.warning(
        'Upload finished with errors',
        `${completed} file(s) uploaded, ${error} failed. Open the Upload page for details.`
      );
    } else if (completed > 0) {
      notificationService.success('Upload complete', `${completed} file(s) uploaded for analysis.`);
    }
  }, []);

  const startWorkers = useCallback(() => {
    const spawn = async () => {
      workersRef.current += 1;
      try {
        while (cursorRef.current < queueRef.current.length) {
          const entry = queueRef.current[cursorRef.current];
          cursorRef.current += 1;
          const outcome = await processEntry(entry);
          statsRef.current[outcome] += 1;
        }
      } finally {
        workersRef.current -= 1;
        if (workersRef.current === 0) {
          void onDrain();
        }
      }
    };

    const backlog = queueRef.current.length - cursorRef.current;
    const toSpawn = Math.min(UPLOAD_CONCURRENCY - workersRef.current, backlog);
    for (let i = 0; i < toSpawn; i++) {
      void spawn();
    }
  }, [onDrain, processEntry]);

  const dispatch = useCallback((files: File[], metadata: UploadMetadata) => {
    if (files.length === 0) return;
    metadataRef.current = metadata;

    const newItems: UploadQueueItem[] = files.map((file) => ({
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      file,
      status: 'pending',
      progress: 0,
      // Stable for this item's lifetime (reused across the relay retry loop) so an automatic
      // re-POST after a lost response de-duplicates server-side instead of creating a second call.
      dedupeId: crypto.randomUUID(),
    }));

    setItems((prev) => [
      // A fresh dispatch replaces finished history so the list stays readable.
      ...prev.filter((i) => i.status !== 'completed' && i.status !== 'error'),
      ...newItems,
    ]);
    queueRef.current.push(...newItems);
    startWorkers();
  }, [startWorkers]);

  // Pause the CJIS inactivity timeout while transfers run (drain-then-logout),
  // and warn on hard navigation/refresh/close — the browser aborts every in-flight
  // request when the page unloads, which is exactly how files were lost in prod.
  const isUploading = items.some(
    (i) => i.status !== 'completed' && i.status !== 'error'
  );
  useEffect(() => {
    setUploadActive(isUploading);
    if (!isUploading) return;

    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', warnBeforeUnload);
      setUploadActive(false);
    };
  }, [isUploading]);

  // Derived summary recomputed only when the queue changes (not on unrelated
  // provider renders), and a stable value reference between item updates.
  const value = useMemo<UploadContextValue>(() => {
    let activeCount = 0;
    let completedCount = 0;
    let errorCount = 0;
    let progressSum = 0;
    let progressTracked = 0;
    for (const item of items) {
      if (item.status === 'completed') {
        completedCount += 1;
        progressSum += 100;
        progressTracked += 1;
      } else if (item.status === 'error') {
        errorCount += 1;
      } else {
        activeCount += 1;
        progressSum += item.progress;
        progressTracked += 1;
      }
    }
    return {
      items,
      isUploading: activeCount > 0,
      activeCount,
      completedCount,
      errorCount,
      overallProgress: progressTracked === 0 ? 0 : Math.round(progressSum / progressTracked),
      dispatch,
    };
  }, [items, dispatch]);

  return <UploadContext.Provider value={value}>{children}</UploadContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useUploadContext = (): UploadContextValue => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUploadContext must be used within an UploadContextProvider');
  }
  return context;
};
